import { DeleteIcon, InfiniteTable } from '@/components/_common';
import {
  useDeleteResourceLimtationPeriod,
  useResourceLimitationPeriodsList,
} from '@/core/domain/enterprise/ResourceLimitationPeriods/ResourceLimitationPeriods.hooks';
import {
  ResourceLimitationPeriod,
  ResourceLimitationPeriodsListQuery,
} from '@/core/domain/enterprise/ResourceLimitationPeriods/ResourceLimitationPeriods.types';
import { useDebounce, useDisclosure } from '@/hooks';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { COLUMNS_WIDTH } from '@/utils/table';
import { Button, Col, Modal, Row, Space } from 'antd';
import { ColumnsType } from 'antd/es/table/interface';
import moment from 'moment';
import { useState } from 'react';
import CompanyResourceLimitationPeriodsFilters from '../CompanyResourceLimitationPeriodsFilters';
import CompanyResourceLimitationPeriodsModal from '../CompanyResourceLimitationPeriodsModal';
import { useRouteMatch } from 'react-router-dom';
import EyeIcon from '@/components/_common/EyeIcon';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';

export default function CompanyResourceLimitationPeriodsList() {
  const removeResourceLimitationPeriod = useDeleteResourceLimtationPeriod();

  const [opened, action] = useDisclosure();

  const [itemEdit, setItemEdit] = useState<ResourceLimitationPeriod>();

  const hasCreatePermission = hasPermissions([
    Authorities.RESOURCE_LIMITATION_PERIODS_ADD,
  ]);
  const hasDeletePermission = hasPermissions([
    Authorities.RESOURCE_LIMITATION_PERIODS_DELETE,
  ]);

  const { companyId } = useRouteMatch<{ companyId: string }>().params;

  const [queryParams, setQueryParams] =
    useState<ResourceLimitationPeriodsListQuery>({
      order: 'ASC',
      ...convertParams<ResourceLimitationPeriodsListQuery>({
        companyId: 'string',
        description: 'string',
        resources: 'string',
      }),
    });

  const queryDebounce = useDebounce(queryParams, 500);

  usePersistSearchParams(queryDebounce);

  const resourceLimitationPeriodList = useResourceLimitationPeriodsList(
    { ...queryParams, companyId: Number(companyId) },
    !!companyId,
  );

  const companies = flatInfiniteData(resourceLimitationPeriodList.data);

  const handleRemoveClick = (company: ResourceLimitationPeriod) => {
    Modal.confirm({
      title: 'Remoção',
      content:
        'Deseja realmente remover o período, esta ação sera irreversível',
      onOk: async () => {
        await removeResourceLimitationPeriod.mutateAsync(company.id);
      },
      okType: 'danger',
    });
  };

  const columns: ColumnsType<ResourceLimitationPeriod> = [
    {
      title: 'Descrição',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      width: COLUMNS_WIDTH.default,
    },
    {
      title: 'Período',
      width: COLUMNS_WIDTH.default,
      render: (_, row) =>
        `${moment(row.startAt).format('DD/MM/YYYY')} | ${moment(
          row.startAt,
        ).format('HH:mm')} > ${moment(row.endAt).format('HH:mm')}`,
    },
    {
      title: 'Recursos',
      width: COLUMNS_WIDTH.small,
      dataIndex: 'resources',
      key: 'resources',
    },
    {
      title: 'Ação',
      key: 'actions',
      width: 100,
      align: 'right',
      fixed: 'right',
      render: (row) => (
        <Space>
          <EyeIcon
            iconProps={{
              onClick: () => {
                action.open();
                setItemEdit(row);
              },
            }}
          />

          <DeleteIcon
            onClick={() => handleRemoveClick(row)}
            disabled={!hasDeletePermission}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Row justify="end" style={{ marginBottom: 24 }}>
        <Col>
          <Button
            type="primary"
            onClick={action.open}
            disabled={!hasCreatePermission}
          >
            Criar indisponibilidade
          </Button>
        </Col>
      </Row>

      <CompanyResourceLimitationPeriodsFilters
        setQueryParams={setQueryParams}
        queryParams={queryParams}
      />

      <InfiniteTable<ResourceLimitationPeriod>
        columns={columns}
        loading={resourceLimitationPeriodList.isFetching}
        dataSource={companies}
        rowKey={(row) => row.id}
        hasNextPage={resourceLimitationPeriodList.hasNextPage}
        fetchNextPage={resourceLimitationPeriodList.fetchNextPage}
      />

      <CompanyResourceLimitationPeriodsModal
        open={opened}
        onCancel={() => {
          action.close();
          setItemEdit(undefined);
        }}
        resourceLimitationPeriodId={itemEdit?.id}
        isView={!!itemEdit}
      />
    </>
  );
}
